import { Loader } from "@googlemaps/js-api-loader";
import query from "utils/query";

export const getMapLoader = () => {
  return new Loader({
    apiKey: process.env.REACT_APP_GOOGLE_API as string,
    version: "weekly",
    libraries: ["places", "geometry"],
    language: "en"
  });
};

export const getBoundsZoomLevel = (
  bounds: google.maps.LatLngBounds,
  mapDim: { width: number; height: number }
): number | null => {
  function latRad(lat: number) {
    const sin = Math.sin((lat * Math.PI) / 180);
    const radX2 = Math.log((1 + sin) / (1 - sin)) / 2;
    return Math.max(Math.min(radX2, Math.PI), -Math.PI) / 2;
  }

  function zoom(mapPx: number, worldPx: number, fraction: number) {
    return Math.floor(Math.log(mapPx / worldPx / fraction) / Math.LN2);
  }

  try {
    const WORLD_DIM = { height: 256, width: 256 };
    const ZOOM_MAX = 22;

    const ne = bounds.getNorthEast();
    const sw = bounds.getSouthWest();

    const latFraction = (latRad(ne.lat()) - latRad(sw.lat())) / Math.PI;

    const lngDiff = ne.lng() - sw.lng();
    const lngFraction = (lngDiff < 0 ? lngDiff + 360 : lngDiff) / 360;

    const latZoom = zoom(mapDim.height, WORLD_DIM.height, latFraction);
    const lngZoom = zoom(mapDim.width, WORLD_DIM.width, lngFraction);

    return Math.min(latZoom, lngZoom, ZOOM_MAX);
  } catch {
    return null;
  }
};

export const geocodeLocationToCity = async (
  value: google.maps.LatLngLiteral
) => {
  if (!google?.maps?.Geocoder) {
    console.error("Geocoder is not initialized");
    return null;
  }
  const geocoder = new google.maps.Geocoder();

  const geocodePromise = geocoder.geocode({
    location: value
  });
  if (!geocodePromise) {
    console.error("Geocoder promise is not defined");
    return null;
  }

  return geocodePromise.then((result) => {
    const locality = result.results.find(
      (r) => r.types.indexOf("locality") !== -1
    );
    if (locality) {
      return {
        placeId: locality.place_id,
        location: locality.geometry.location.toJSON(),
        description: getLocalityName(locality)
      };
    } else {
      return null;
    }
  });
};

export const getLocalityName = (locality: google.maps.GeocoderResult) => {
  const city =
    locality.address_components.find((c) => c.types.includes("locality"))
      ?.long_name ?? locality.address_components[0].long_name;

  const country = locality.address_components.find((c) =>
    c.types.includes("country")
  )?.long_name;

  return `${city}, ${country}`;
};

export const findClosestRadius = (value: number, radii: number[]): number => {
  const difference: number[] = [];
  radii.forEach((r) => difference.push(Math.abs(r - value)));

  const minDifference = difference.reduce((min, value) => {
    return Math.min(min, value);
  }, Infinity);

  return radii[difference.indexOf(minDifference)];
};

export const isPositiveInteger = (value: string): boolean => {
  if (!/^[0-9]*$/.test(value)) return false;
  return Number(value) > 0;
};

export const generateStaticMapImageUrl = ({
  location,
  zoom,
  width,
  height
}: {
  location: google.maps.LatLngLiteral;
  zoom: number;
  width: number;
  height: number;
}) => {
  return (
    `https://maps.googleapis.com/maps/api/staticmap?` +
    query.stringify({
      center: `${location.lat},${location.lng}`,
      zoom: zoom,
      size: `${width}x${height}`,
      maptype: "terrain",
      key: process.env.REACT_APP_GOOGLE_API,
      style: "feature:poi|element:labels|visibility:off",
      markers: `color:red|${location.lat},${location.lng}`
    })
  );
};

export const generateGoogleMapsUrl = ({
  location,
  zoom
}: {
  location: google.maps.LatLngLiteral;
  zoom: number;
}) => {
  return `https://www.google.com/maps/?q=${location.lat},${location.lng}&z=${zoom}`;
};
